import { CYLINDER_RANGE, SPHERE_RANGE, dioptre, isWithin } from '../dioptre.js';
// ---------------------------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------------------------
/**
 * Characters that mean the same thing and are not the same character.
 *
 * A prescription typed in Word arrives with a U+2212 minus and a U+00D7 multiplication sign, and a
 * regex built for ASCII silently sees no minus at all, which under the ambiguous-sign rule above
 * would reject the entire prescription. That is a correct refusal for a wrong reason, so it is
 * fixed here rather than allowed to look like a parser that does not work.
 *
 * The Arabic-Indic digits are there because this is Pakistan. `DESIGN-TOKENS.md` §3 requires
 * Western digits everywhere including the Urdu interface, so they are normalised on the way in
 * rather than being a second numeric path through everything downstream.
 */
const CHARACTER_EQUIVALENTS = [
    [/[\u2212\u2012\u2013\u2014\u2015\uFE63\uFF0D]/g, '-'],
    [/[\uFF0B\uFE62]/g, '+'],
    [/[\u00D7\u2715\u2716\u2A2F]/g, 'x'],
    [/[\u066B\uFF0E]/g, '.'],
];
function normalise(raw) {
    let text = raw.normalize('NFKC');
    for (const [pattern, replacement] of CHARACTER_EQUIVALENTS) {
        text = text.replace(pattern, replacement);
    }
    // NFKC does not fold Arabic-Indic or Extended Arabic-Indic digits, so fold them by hand.
    text = text.replace(/[\u0660-\u0669]/g, (m) => String(m.charCodeAt(0) - 0x0660));
    text = text.replace(/[\u06f0-\u06f9]/g, (m) => String(m.charCodeAt(0) - 0x06f0));
    return text;
}
// ---------------------------------------------------------------------------------------------
// Splitting into eyes
// ---------------------------------------------------------------------------------------------
/**
 * The right eye, in the notations a Pakistani counter actually receives.
 *
 * `oculus dexter` and its abbreviations, the English word, and the Urdu. `DESIGN-TOKENS.md` §9
 * `RX-06` requires the grid to label rows "od / right" because new staff do not know the Latin,
 * and the same is true of whoever wrote the paper.
 */
const OD_MARKER = /\b(?:od|o\.d\.|r|re|rt|right)\b[\s:.]*|\u062f\u0627\u0626\u06cc\u06ba|\u062f\u0627\u06c1\u0646\u06cc/giu;
const OS_MARKER = /\b(?:os|o\.s\.|l|le|lt|left)\b[\s:.]*|\u0628\u0627\u0626\u06cc\u06ba/giu;
/**
 * Cut the input into at most one segment per eye.
 *
 * Two shapes have to work, because both are written. One line per eye with a marker at the start,
 * and both eyes on one line separated by markers. Finding marker positions and slicing between them
 * handles both without caring about line breaks.
 */
function segment(text) {
    const marks = [];
    for (const [pattern, eye] of [
        [OD_MARKER, 'od'],
        [OS_MARKER, 'os'],
    ]) {
        pattern.lastIndex = 0;
        let m;
        while ((m = pattern.exec(text)) !== null) {
            marks.push({ eye, start: m.index, end: m.index + m[0].length });
            if (m[0].length === 0)
                pattern.lastIndex += 1;
        }
    }
    if (marks.length === 0) {
        // No markers. Lines carry the convention instead: §9's grid puts OD above OS, and so does every
        // written prescription, so the first line is the right eye. Recorded as an assumption.
        const lines = text
            .split(/[\n\r;|]+/)
            .map((l) => l.trim())
            .filter((l) => /\d/.test(l));
        if (lines.length >= 2) {
            return [
                { eye: 'od', text: lines[0] },
                { eye: 'os', text: lines[1] },
            ];
        }
        return lines.length === 1 ? [{ eye: 'unknown', text: lines[0] }] : [];
    }
    marks.sort((a, b) => a.start - b.start);
    const out = [];
    const seen = new Set();
    for (let i = 0; i < marks.length; i += 1) {
        const mark = marks[i];
        if (seen.has(mark.eye))
            continue;
        seen.add(mark.eye);
        const next = marks[i + 1];
        out.push({ eye: mark.eye, text: text.slice(mark.end, next ? next.start : undefined) });
    }
    return out;
}
// ---------------------------------------------------------------------------------------------
// Reading one eye
// ---------------------------------------------------------------------------------------------
/** `add`, `near`, `nv` and the Urdu, each optionally followed by a colon. */
const ADD_MARKER = /\b(add|near|nv|nva|reading)\b\s*:?\s*/iu;
/** An axis introduced by its marker: `x180`, `X 180`, `@ 180`, `ax 180`, `axis 180`. */
const AXIS_MARKED = /(?:\bx\b|x(?=\s*\d)|@|\bax\b|\baxis\b)\s*(\d{1,3})\b/i;
/** Zero power, in the four ways it is written. `DS` is "dioptres sphere", meaning no cylinder. */
const PLANO = /\b(plano|pl|pln)\b/i;
function toDioptre(value, field, range, eye, text, rejections) {
    let d;
    try {
        d = dioptre(value);
    }
    catch {
        // `dioptre()` rejects anything off the 0.25 grid rather than rounding to it, which is the whole
        // reason it exists. A pasted -2.30 is a transcription error, and rounding it to -2.25 would
        // launder that error into the record.
        rejections.push({ text, reason: 'not_a_quarter_step', eye, field });
        return undefined;
    }
    if (range && !isWithin(d, range)) {
        rejections.push({ text, reason: 'out_of_range', eye, field });
        return undefined;
    }
    return d;
}
const POWER_TOKEN = /([+-])?\s*(\d+(?:\.\d+)?)/g;
/**
 * Read one eye's row.
 *
 * Positional across **every** number rather than only the signed ones, and that distinction is
 * the whole correctness of the function. Assigning positions among the signed values alone means
 * one unsigned sphere silently promotes the cylinder into the sphere slot, so "OD 2.25 -0.75"
 * would record a sphere of -0.75: a value nobody wrote, in the field RX-03 calls the most
 * expensive typo in the product. Judging each number by the position it occupies means an
 * unsigned sphere is refused as a sphere rather than shifted somewhere it happens to fit.
 */
function parseEye(raw, eye, rejections, assumptions) {
    // "Plano" is zero and has exactly one reading, so it becomes a signed zero and takes its place
    // in the sequence like any other power. That keeps "plano -0.50 x 90" reading as sphere then
    // cylinder rather than as a lone cylinder sitting in the sphere position.
    let text = raw.replace(PLANO, '+0.00').replace(/\b(?:ds|sph|cyl|dsph|dc)\b/gi, ' ');
    let axis;
    let add;
    // Marked fields come out first so their numbers cannot occupy a power position.
    const axisMatch = AXIS_MARKED.exec(text);
    if (axisMatch) {
        const value = Number(axisMatch[1]);
        if (value >= 1 && value <= 180) {
            axis = value;
        }
        else {
            // Appendix I gives axis as 1 to 180. Someone writing 0 probably meant 180, and "probably"
            // is not good enough for the meridian a lens is ground on.
            rejections.push({
                text: axisMatch[0].trim(),
                reason: 'axis_out_of_range',
                eye,
                field: 'axis',
            });
        }
        text = `${text.slice(0, axisMatch.index)} ${text.slice(axisMatch.index + axisMatch[0].length)}`;
    }
    const addMatch = ADD_MARKER.exec(text);
    if (addMatch) {
        const after = text.slice(addMatch.index + addMatch[0].length);
        const num = /^\s*([+-])?\s*(\d+(?:\.\d+)?)/.exec(after);
        if (num) {
            // An add is a near addition and positive by definition, so an unsigned one has a single
            // reading and there is nothing to guess. A written minus is respected rather than
            // corrected: an occupational or degressive lens can carry one, and Appendix I judges it.
            const value = Number(`${num[1] ?? '+'}${num[2]}`);
            add = toDioptre(value, 'add', undefined, eye, num[0].trim(), rejections);
            text = text.slice(0, addMatch.index) + ' ' + after.slice(num[0].length);
        }
    }
    POWER_TOKEN.lastIndex = 0;
    const tokens = [...text.matchAll(POWER_TOKEN)].map((m) => ({
        signed: m[1] !== undefined,
        value: Number(`${m[1] ?? ''}${m[2]}`),
        text: m[0].replace(/\s+/g, ''),
    }));
    let sphere;
    let cylinder;
    const isAxisShaped = (token) => !token.signed &&
        Number.isInteger(token.value) &&
        token.value >= 1 &&
        token.value <= 180 &&
        !token.text.includes('.');
    tokens.forEach((token, position) => {
        // Sphere and cylinder are the two fields with two genuine readings, so both demand a sign.
        // A parser that assumes minus is right most of the time, which is exactly what makes it
        // dangerous: the dispenser stops re-reading a field the software fills in correctly.
        if (position === 0) {
            if (token.signed) {
                sphere = toDioptre(token.value, 'sphere', SPHERE_RANGE, eye, token.text, rejections);
            }
            else {
                rejections.push({ text: token.text, reason: 'ambiguous_sign', eye, field: 'sphere' });
            }
            return;
        }
        if (position === 1) {
            if (token.signed) {
                cylinder = toDioptre(token.value, 'cylinder', CYLINDER_RANGE, eye, token.text, rejections);
            }
            else {
                rejections.push({ text: token.text, reason: 'ambiguous_sign', eye, field: 'cylinder' });
            }
            return;
        }
        if (axis === undefined && cylinder !== undefined && isAxisShaped(token)) {
            // The one unsigned number with a single reading: a sphere or a cylinder would have carried
            // a sign to reach this position, so a bare whole number after a cylinder is an axis.
            axis = token.value;
            assumptions.push({
                text: token.text,
                assumption: `read as the ${eye.toUpperCase()} axis, being a whole number in 1 to 180 following a cylinder`,
            });
            return;
        }
        if (add === undefined && token.signed) {
            // A third signed power with no add label is the near addition, which is how a great many
            // prescriptions are written: "-2.25 -0.75 x 180 +2.00".
            add = toDioptre(token.value, 'add', undefined, eye, token.text, rejections);
            assumptions.push({
                text: token.text,
                assumption: `read as the near addition for ${eye.toUpperCase()}, being the third power with no add label`,
            });
            return;
        }
        rejections.push({
            text: token.text,
            reason: token.signed ? 'unrecognised' : 'ambiguous_sign',
            eye,
        });
    });
    const parsed = {};
    if (sphere !== undefined)
        parsed.sphere = sphere;
    if (cylinder !== undefined)
        parsed.cylinder = cylinder;
    if (axis !== undefined)
        parsed.axis = axis;
    if (add !== undefined)
        parsed.add = add;
    return parsed;
}
/**
 * Parse a pasted prescription.
 *
 * Never throws: a paste is user input and the caller is a keystroke handler. Everything it could
 * not use comes back in `rejections`, and everything it inferred comes back in `assumptions`, so
 * the grid can show both instead of the dispenser discovering later that half the paste vanished.
 */
export function parsePastedPrescription(input) {
    const rejections = [];
    const assumptions = [];
    const text = normalise(input);
    if (text.trim() === '')
        return { rejections, assumptions };
    const segments = segment(text);
    const result = {};
    for (const seg of segments) {
        if (seg.eye === 'unknown') {
            // One line, no marker, nothing to say which eye it is. §9's grid starts at OD and RXW-002's
            // tab order is OD then OS, so it goes in the first row, and the dispenser is told.
            const parsed = parseEye(seg.text, 'od', rejections, assumptions);
            if (Object.keys(parsed).length > 0) {
                result.od = parsed;
                assumptions.push({
                    text: seg.text.trim(),
                    assumption: 'no eye was named, so this was read as OD (right). Check before saving.',
                });
            }
            continue;
        }
        const parsed = parseEye(seg.text, seg.eye, rejections, assumptions);
        if (Object.keys(parsed).length > 0)
            result[seg.eye] = parsed;
    }
    const out = { rejections, assumptions };
    if (result.od)
        out.od = result.od;
    if (result.os)
        out.os = result.os;
    return out;
}
//# sourceMappingURL=paste.js.map